import { ReactNode, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { IconAlert } from '@avantstay/backoffice-vectors'

import { Checkbox } from '../../../Checkbox'

import { numberWithCommas, pluralize } from '../../../../utils'
import { Button } from '../../../Button'
import { Modal } from '../../../Modal'
import { TextField } from '../../../TextField'
import { SNormal } from '../../../Typography'
import * as S from './ConfirmationModal.styles'

export interface ConfirmationFormValue {
  inputValue: string
  includeSensitiveInformation: boolean
}

export interface ConfirmationModalProps {
  isOpen: boolean
  numberOfRecordsToExport?: number
  recordsLimit?: number
  recordsLimitMessage?: ReactNode
  sensitiveInformationCheckbox?: { label: string }
  onConfirm: (value?: Pick<ConfirmationFormValue, 'includeSensitiveInformation'>) => void
  onClose: () => void
}

const expectedInputValue = 'Export CSV'

export const DEFAULT_EXPORT_RECORDS_LIMIT = 2000

const DATA_REQUEST_CHANNEL_URL = 'https://avantstay.slack.com/archives/C030JAKR3NW'

export const ConfirmationModal = ({
  isOpen,
  numberOfRecordsToExport,
  recordsLimit = DEFAULT_EXPORT_RECORDS_LIMIT,
  recordsLimitMessage,
  sensitiveInformationCheckbox,
  onConfirm,
  onClose,
}: ConfirmationModalProps) => {
  const form = useForm<ConfirmationFormValue>({
    defaultValues: { inputValue: '', includeSensitiveInformation: false },
  })

  const onSubmit = ({ includeSensitiveInformation }: ConfirmationFormValue) => {
    onConfirm(sensitiveInformationCheckbox ? { includeSensitiveInformation } : undefined)
  }

  useEffect(() => {
    if (!isOpen) {
      form.reset()
    }
  }, [isOpen, form])

  const inputValue = form.watch('inputValue')

  const isOverLimit = numberOfRecordsToExport != null && numberOfRecordsToExport > recordsLimit

  const defaultLimitMessage = (
    <>
      Exports are capped at {numberWithCommas(recordsLimit)} rows
      {numberOfRecordsToExport != null
        ? `, and this view has ${numberWithCommas(numberOfRecordsToExport)}`
        : ''}
      . Narrow your filters to export, or request the full dataset in{' '}
      <S.DataRequestLink href={DATA_REQUEST_CHANNEL_URL} target="_blank" rel="noopener noreferrer">
        #data-request
      </S.DataRequestLink>
      .
    </>
  )

  if (!isOpen) return null

  return (
    <Modal overflowHidden onCloseModal={onClose}>
      <S.ModalContainer onSubmit={form.handleSubmit(onSubmit)}>
        <S.ModalHeader>{isOverLimit ? 'Export limit reached' : 'Confirmation Required'}</S.ModalHeader>

        <S.ModalContent>
          {isOverLimit ? (
            <S.LimitWarning>
              <IconAlert />
              <div>{recordsLimitMessage ?? defaultLimitMessage}</div>
            </S.LimitWarning>
          ) : (
            <>
              {numberOfRecordsToExport ? (
                <p>
                  You are about to export{' '}
                  <strong>
                    {`${numberWithCommas(numberOfRecordsToExport)} ${pluralize(numberOfRecordsToExport, 'records', 'record')}`}
                  </strong>{' '}
                  to a CSV file.
                </p>
              ) : (
                <p>You are about to export the records to a CSV file.</p>
              )}
              <SNormal>
                To proceed, type <u>Export CSV</u> in the field below. Note: Text is case sensitive.
              </SNormal>

              <Controller
                control={form.control}
                name="inputValue"
                render={({ field, fieldState }) => (
                  <TextField.Small
                    {...field}
                    autoFocus
                    id="export-csv-confirmation-field"
                    autoComplete="off"
                    error={fieldState.error?.message}
                  />
                )}
                rules={{
                  validate: value => {
                    const isValidInputValue = !!value && value === expectedInputValue
                    return isValidInputValue ? undefined : 'Please, provide the correct text'
                  },
                }}
              />

              {sensitiveInformationCheckbox ? (
                <Controller
                  control={form.control}
                  name="includeSensitiveInformation"
                  render={({ field }) => (
                    <S.CheckboxWrapper>
                      <Checkbox.Regular
                        {...field}
                        hasFullWidth
                        id="confirmation-modal-checkbox"
                        title={sensitiveInformationCheckbox.label}
                        className={S.confirmationModalCheckboxClassName}
                        labelStyle={{ whiteSpace: 'pre-line' }}
                      />
                    </S.CheckboxWrapper>
                  )}
                />
              ) : null}
            </>
          )}
        </S.ModalContent>

        <S.ModalFooter>
          {isOverLimit ? (
            <Button.Primary type="button" onClick={onClose}>
              Close
            </Button.Primary>
          ) : (
            <>
              <Button.Secondary type="reset" onClick={onClose}>
                Cancel
              </Button.Secondary>
              <Button.Primary type="submit" disabled={inputValue !== expectedInputValue}>
                Confirm
              </Button.Primary>
            </>
          )}
        </S.ModalFooter>
      </S.ModalContainer>
    </Modal>
  )
}
